/**
 * 邀请链接处理页面
 * 用户通过邀请链接访问此页面，可以接受或拒绝邀请
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { invitationApi, authApi } from '../services/api';
import LoginDialog from '../components/LoginDialog';

const InviteAccept: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteInfo, setInviteInfo] = useState<{
    group_name?: string;
    inviter_name?: string;
  } | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const inviteCode = searchParams.get('code');

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await authApi.getCurrentUser();
        setIsLoggedIn(true);
      } catch {
        setIsLoggedIn(false);
      } finally {
        setAuthChecked(true);
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    // 等待登录检查完成
    if (!authChecked) return;
    
    if (!inviteCode) {
      setError('无效的邀请链接');
      setLoading(false);
      return;
    }

    // 未登录时停止加载，显示登录按钮
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }

    // 已登录，验证邀请码
    const verifyInvite = async () => {
      try {
        const res = await invitationApi.verifyCode(inviteCode);
        setInviteInfo({
          group_name: res.data?.group_name,
          inviter_name: res.data?.inviter_name,
        });
      } catch (e: any) {
        setError(e?.response?.data?.detail || '邀请码无效或已过期');
      } finally {
        setLoading(false);
      }
    };

    verifyInvite();
  }, [inviteCode, isLoggedIn, authChecked]);

  // 登录成功后的回调
  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
    setShowLoginModal(false);
  };

  const handleAccept = async () => {
    if (!inviteCode) return;
    setProcessing(true);
    try {
      await invitationApi.joinByCode(inviteCode);
      navigate('/project-groups');
    } catch (e: any) {
      setError(e?.response?.data?.detail || '接受邀请失败');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = () => {
    navigate('/projects');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">验证邀请中...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <UserGroupIcon className="w-16 h-16 text-blue-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">您收到了一个项目组邀请</h1>
          <p className="text-gray-500 mb-6">请先登录以接受或拒绝邀请</p>
          <button
            onClick={() => setShowLoginModal(true)}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            登录
          </button>
        </div>

        {/* 使用统一的登录弹框 */}
        <LoginDialog
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onSuccess={handleLoginSuccess}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">邀请无效</h1>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/projects')}
            className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            返回首页
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4">
        <div className="text-center mb-6">
          <UserGroupIcon className="w-16 h-16 text-blue-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">项目组邀请</h1>
          {inviteInfo?.inviter_name && (
            <p className="text-gray-600">
              <span className="font-medium">{inviteInfo.inviter_name}</span> 邀请您加入项目组
            </p>
          )}
          {inviteInfo?.group_name && (
            <p className="text-lg font-semibold text-blue-600 mt-2">
              「{inviteInfo.group_name}」
            </p>
          )}
        </div>

        <div className="space-y-3">
          <button
            onClick={handleAccept}
            disabled={processing}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <CheckCircleIcon className="w-5 h-5" />
            {processing ? '处理中...' : '接受邀请'}
          </button>
          <button
            onClick={handleReject}
            disabled={processing}
            className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <XCircleIcon className="w-5 h-5" />
            拒绝
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteAccept;
